// components/LoginButton.tsx
'use client';

import React, { useEffect, useState } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { IoClose } from 'react-icons/io5';
import { SignInOptions } from './sign-up/sign-up-options';
import Login from '@/public/icons/login.svg';
import { useUiContent } from '@/components/ui-content-provider';
import { useAuth } from '@/components/auth-context';
import { addNextCookieAction, clearCookieAction } from '@/server/articles/uid-cookie-actions';
import { auth } from '@/lib/firebase';

interface LoginButtonProps {
  locale: LocaleDetails;
}

export const LoginButton: React.FC<LoginButtonProps> = ({ locale }) => {
  const ui = useUiContent();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const parentRef = React.useRef<HTMLDivElement>(null);

  // useAuth() registers demand, so on this articles route the Firebase SDK
  // loads on mount and login state resolves without user interaction.
  const { loggedIn, user } = useAuth();

  // Function to open the modal
  const openModal = () => {
    setIsModalOpen(true);
  };

  // Function to close the modal
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Function to handle sign out
  const handleSignOut = async () => {
    await auth.signOut();
    await clearCookieAction();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (parentRef.current && !parentRef.current.contains(event.target as Node)) {
        closeModal();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Set the cookie with the encrypted uid when logged in
  useEffect(() => {
    if (loggedIn && user) {
      addNextCookieAction(user.uid).catch((error) => {
        console.error('Failed to set uid cookie:', error);
      });
    }
  }, [loggedIn, user]);

  // TODO: AUTH-001 - ENABLE LOGIN BUTTON IN PRODUCTION ONCE COMPLETED
  // See INCEPTIONAI_BACKLOG.md for full requirements and business impact
  // Login functionality is currently disabled in production to avoid performance impact
  // Before enabling: validate with dev team that the auth system is fully completed and tested
  // To enable: remove the production check below (process.env.NODE_ENV === 'production')
  
  // Hide login button in development environment
  // if (process.env.NODE_ENV === 'development') {
  //   return null;
  // }

  return (
    <>
      {/* Login/Disconnect Button */}
      <button
        onClick={loggedIn ? handleSignOut : openModal}
        className={
          'fixed right-2 top-[4.3rem] z-30 flex items-center justify-center rounded-full border border-neutral-900/10 bg-light dark:border-light dark:bg-dark xs:right-5'
        }
        aria-label={loggedIn ? 'Disconnect' : 'Login'}
        title={loggedIn ? 'Disconnect' : 'Login'}
      >
        <p className="pl-2.5 pr-1.5 text-sm font-semibold text-neutral-900 dark:text-light">
          {loggedIn ? ui.disconnect : ui.login}
        </p>
        <Login
          className={
            'my-auto h-8 w-8 min-w-8 rounded-full border border-neutral-900/10 fill-dark pl-0.5 pt-1.5 dark:border-light dark:fill-light'
          }
        />
      </button>

      {/* Modal Overlay */}
      {!loggedIn && isModalOpen && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className="z-10 mx-4 w-full max-w-lg overflow-hidden rounded-lg bg-white px-4 py-12 shadow-lg dark:bg-dark-inside"
            ref={parentRef}
          >
            {/* Close Button */}
            <button onClick={closeModal} className="absolute right-3 top-3 focus:outline-none" aria-label="Close modal">
              <IoClose size={24} />
            </button>

            {/* Sign-In Options */}
            <SignInOptions locale={locale} />
          </div>
        </div>
      )}
    </>
  );
};
